/**
 * Publications: every post on every phone (plan Stage 2, owner 2026-10-09 — "історія … скільки, що, коли").
 *
 * Ferma's own postings appear by themselves (the service turns each finished posting into one); posts made
 * by hand or by Claude are recorded over MCP. What a person fills in here is what the service cannot know
 * by itself: the post's link (which also settles the account) and, when a phone holds several accounts,
 * which one posted. What was posted, where and when is history and is not editable.
 */

import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { Direction, MarketingAccount, Publication, TargetLink } from '../../api/types'
import { useT } from '../../i18n'
import { useApp } from '../../lib/context'
import { usePoll } from '../../lib/poll'
import { Card, Chips, Empty, Status } from '../../ui/components'
import { Person } from '../../ui/Person'
import { PhoneLink } from '../../ui/PhoneLink'
import { DirectionChip, useDirectionName, useDirections } from './directions'
import { PostStatsLine } from './ProfileReading'

type Need = 'all' | 'noLink' | 'noAccount' | 'noTarget'

function errorText(e: unknown): string {
  const d = (e as { detail?: { detail?: { hint?: string } } })?.detail?.detail
  return d?.hint ?? (e instanceof Error ? e.message : String(e))
}

function dayOf(iso?: string | null): string {
  return iso ? new Date(iso).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' }) : '—'
}

/** `direction` given — only that direction's posts (its tab on the direction's page). */
export function PublicationsPanel({ direction: fixed }: { direction?: string } = {}) {
  const t = useT()
  const p = t.publications
  const { api, authEpoch } = useApp()
  const dirs = useDirections()
  const nameOf = useDirectionName()
  const [chosen, setDirection] = useState<string>('all')
  const direction = fixed ?? chosen
  const [need, setNeed] = useState<Need>('all')
  const pubs = usePoll(() => api.publications({ direction: direction === 'all' ? undefined : direction, limit: 1000, on_phone: true }),
                       60000, [api, authEpoch, direction])
  const accounts = usePoll(() => api.accounts(), 60000, [api, authEpoch])
  const targets = usePoll(() => api.links(), 60000, [api, authEpoch])
  const [params, setParams] = useSearchParams()
  const onlyLink = params.get('link')
  const onlyTarget = (targets.data?.links ?? []).find((x) => x.link_id === onlyLink)

  const all = pubs.data?.publications ?? []
  const shown = all.filter((x) => (!onlyLink || (x.link_ids ?? []).includes(onlyLink))
    && (need === 'all' || (need === 'noLink' ? !x.url : need === 'noTarget' ? !(x.link_ids ?? []).length : !x.account_id)))
  const byDay = useMemo(() => {
    const groups: { day: string; rows: Publication[] }[] = []
    for (const row of shown) {
      const day = dayOf(row.published_at)
      let last = groups[groups.length - 1]
      if (!last || last.day !== day) {
        last = { day, rows: [] }
        groups.push(last)
      }
      last.rows.push(row)
    }
    return groups
  }, [shown])
  const onPhone = (serial: string) => (accounts.data?.accounts ?? []).filter((a) => a.serial === serial && a.state !== 'logged_out')

  return (
    <>
      <Card title={p.title} sub={p.sub}>
        <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
          {!fixed && <Chips value={direction} onChange={setDirection} options={[
            { id: 'all', label: t.marketing.allDirections },
            ...dirs.list.map((d) => ({ id: d.direction_id, label: nameOf(d) })),
          ]} />}
          <Chips<Need> value={need} onChange={setNeed} options={[
            { id: 'all', label: p.all }, { id: 'noLink', label: p.noLink }, { id: 'noAccount', label: p.noAccount },
            { id: 'noTarget', label: p.noTarget },
          ]} />
        </div>
        {onlyLink && (
          <p className="tiny">
            {p.onlyLink(onlyTarget ? (onlyTarget.name || onlyTarget.url) : onlyLink)}{' '}
            <button className="quiet tiny" onClick={() => setParams({})}>{p.showAll}</button>
          </p>
        )}
        {pubs.data && (
          <p className="tiny dim">{p.summary(all.length, all.filter((x) => !x.url).length, all.filter((x) => !x.account_id).length)}</p>
        )}
        <Status text={pubs.error ? String(pubs.error) : null} bad />
      </Card>

      {pubs.data && shown.length === 0 && <Card><Empty>{p.empty}</Empty></Card>}
      {byDay.map((g) => (
        <Card key={g.day} title={g.day} sub={p.day(g.rows.length)}>
          <div className="pub-list">
            {g.rows.map((row) => (
              <PublicationRow key={row.publication_id} pub={row} direction={dirs.byId.get(row.direction_id ?? '')}
                              accounts={onPhone(row.serial)} targets={targets.data?.links ?? []}
                              onChanged={() => { pubs.refresh(); targets.refresh() }} />
            ))}
          </div>
        </Card>
      ))}
    </>
  )
}

function PublicationRow({ pub, direction, accounts, targets, onChanged }: {
  pub: Publication
  direction?: Direction
  accounts: MarketingAccount[]
  targets: TargetLink[]
  onChanged: () => void
}) {
  const t = useT()
  const p = t.publications
  const pr = t.profile
  const { api } = useApp()
  const [link, setLink] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const save = async (body: { url?: string; account_id?: string; link_id?: string | null }) => {
    setProblem(null)
    try {
      await api.editPublication(pub.publication_id, body)
      setLink(null)
      onChanged()
    } catch (e) {
      setProblem(errorText(e))
    }
  }
  const time = pub.published_at ? new Date(pub.published_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '—'
  const what = pub.kind === 'text' ? pub.text || pub.caption : pub.video_name || pub.video_id || p.video
  return (
    <div className="pub-row">
      <span className="pub-time mono">{time}</span>
      <div className="pub-main">
        <div className="pub-head">
          <DirectionChip direction={direction} />
          <PhoneLink deviceId={pub.device_name ? pub.device_id : null} name={pub.device_name} serial={pub.serial} />
          {pub.account_id && pub.account_handle ? (
            <a className="text-link" href={`https://www.tiktok.com/@${pub.account_handle}`} target="_blank" rel="noreferrer noopener"
               title={pub.account_by ? p.accountBy[pub.account_by] : undefined}>{pub.account_label || `@${pub.account_handle}`}</a>
          ) : pub.account_id ? (
            <span title={pub.account_by ? p.accountBy[pub.account_by] : undefined}>{pub.account_label}</span>
          ) : accounts.length > 1 ? (
            <select aria-label={p.pickAccount} value="" onChange={(e) => e.target.value && void save({ account_id: e.target.value })}>
              <option value="">{p.pickAccount}</option>
              {accounts.map((a) => <option key={a.account_id} value={a.account_id}>{a.label}</option>)}
            </select>
          ) : (
            <span className="tiny warn-text">{accounts.length === 0 ? p.noAccounts : p.noAccount}</span>
          )}
          <span className="pub-kind tiny">{pub.kind === 'text' ? p.text : p.video}</span>
        </div>
        <div className="pub-what">
          <span className="pub-title">{what}</span>
          {pub.overlay_text && <span className="tiny dim"> · {p.onWords}: «{pub.overlay_text}»</span>}
        </div>
        {pub.caption && pub.kind === 'video' && <div className="tiny dim pub-caption">{pub.caption}</div>}
        <div className="pub-target tiny">
          <span className="dim">{p.target}: </span>
          {(pub.links ?? []).length > 0 ? (
            <>
              {pub.links!.map((x, i) => (
                <span key={x.link_id}>{i > 0 && ', '}<b>{x.name || x.url}</b>{x.name && <span className="dim mono"> {x.url}</span>}</span>
              ))}
              <span className="dim"> · {pub.link_from === 'person' ? p.targetByPerson : p.targetFromText}</span>
              {pub.link_from === 'person' && (
                <button className="quiet tiny" onClick={() => void save({ link_id: null })}>{p.targetReset}</button>
              )}
            </>
          ) : targets.filter((x) => !x.retired).length > 0 ? (
            <select aria-label={p.pickTarget} value="" onChange={(e) => e.target.value && void save({ link_id: e.target.value })}>
              <option value="">{p.pickTarget}</option>
              {targets.filter((x) => !x.retired).map((x) => (
                <option key={x.link_id} value={x.link_id}>{x.name ? `${x.name} — ${x.url}` : x.url}</option>
              ))}
            </select>
          ) : (
            <span className="warn-text">{p.noTargets}</span>
          )}
        </div>
        {pub.stats && (
          <div className="pub-stats">
            <PostStatsLine stats={pub.stats} />
            {pub.stats_at && <span className="tiny dim"> · {pr.statsAt(new Date(pub.stats_at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }))}</span>}
            {pub.ai_label && <span className="tiny dim"> · {pr.aiLabel}</span>}
          </div>
        )}
        <div className="pub-meta tiny dim">
          <span>{p.source[pub.source] ?? pub.source}</span>
          {pub.by && <> · <Person who={pub.by} /></>}
          {!pub.device_name && <> · {p.phoneGone}</>}
        </div>
        {problem && <div className="tiny bad-text">{problem}</div>}
      </div>
      <div className="pub-link">
        {pub.url ? (
          <a className="text-link" href={pub.url} target="_blank" rel="noreferrer noopener">{p.open} ↗</a>
        ) : link === null ? (
          <button className="quiet tiny" onClick={() => setLink('')}>{p.addLink}</button>
        ) : (
          <form className="pub-link-form" onSubmit={(e) => { e.preventDefault(); void save({ url: link.trim() }) }}>
            <input value={link} onChange={(e) => setLink(e.target.value)} placeholder={p.linkPlaceholder}
                   aria-label={p.addLink} autoFocus />
            <button className="tiny" type="submit" disabled={!link.trim()}>{p.save}</button>
            <button className="quiet tiny" type="button" onClick={() => { setLink(null); setProblem(null) }}>{p.cancel}</button>
          </form>
        )}
      </div>
    </div>
  )
}
